import { textListPrefixFromProjection } from './textListPrefixRead';
import type { CanvasTextListPrefix } from '@hector/shared-types';
import type {
  CanvasInspectImage,
  CanvasInspectionProjection,
  CanvasInspectionProjectionReceipt,
} from '@hector/shared-types';
import type { SuperDocDocumentApi } from './superDocDocumentApi';

/** Same per-block model boundary as the public canvas receipt schema. */
const MAX_MODEL_TEXT_LENGTH = 2_000;
const MAX_PROJECTION_DIAGNOSTICS = 32;
const MAX_PROJECTION_ANNOTATION_IDS = 64;

type CanvasProjectionBlockCommon = {
  nodeType: string;
  /** Native Markdown coordinates, retained locally to build read-only compact ranges. */
  outputRange: { start: number; end: number };
  /** Whole provider-owned output span, retained only for local selector matching. */
  fullText: string;
  /** Bounded form that is safe to put in the agent receipt. */
  text: string;
  textTruncated?: true;
  parentBlockId?: string;
  changeIds?: string[];
  commentIds?: string[];
  /** Les images que ce bloc porte, posées par `nommerLesImagesDeLaProjection` (`imagesDuCorps.ts`). */
  images?: CanvasInspectImage[];
};

type CanvasPublicProjectionBlock = CanvasProjectionBlockCommon & {
  identity: 'public';
  nodeId: string;
};

type CanvasUnavailableProjectionBlock = CanvasProjectionBlockCommon & {
  identity: 'unavailable';
  identityUnavailableReason: 'positionDerivedFallback';
};

/** A block exactly as the native Markdown projection addressed it. */
export type CanvasProjectionBlock = CanvasPublicProjectionBlock | CanvasUnavailableProjectionBlock;

/** Projection blocks after resolver-only full text has been removed. */
export type ModelSafeCanvasProjectionBlock =
  (Omit<CanvasPublicProjectionBlock, 'fullText' | 'outputRange'>
  | Omit<CanvasUnavailableProjectionBlock, 'fullText' | 'outputRange'>) & { textListPrefix?: CanvasTextListPrefix };

export interface CanvasMarkdownProjection {
  projection: CanvasInspectionProjectionReceipt;
  /** Native Markdown returned once for a complete-document read (#1606). */
  content: string;
  /** Top-level projection blocks only: nested entries would duplicate table/cell text in the model context. */
  blocks: CanvasProjectionBlock[];
  /**
   * `true` only when SuperDoc supplied every usable top-level block boundary.
   * The readable document fallback deliberately cannot answer a targeted
   * `textSearch`: matching it would return an arbitrary leading excerpt of the
   * whole document rather than the passage that actually matched (#1605).
   */
  hasCompleteTopLevelBlockMap: boolean;
  /**
   * Every public block id of the body map, nested entries included: an image
   * anchored to one of them is a body image (`imagesDuCorps.ts`).
   */
  blocsDuCorps: ReadonlySet<string>;
  /**
   * Where the provider announced an image placeholder in the body: the block
   * that carries it and that block's range in `content` (the provider reports
   * the block range, not the placeholder's own position).
   */
  emplacementsDImage: ReadonlyArray<{ blockId: string; debut: number; fin: number }>;
}

/** A native projection cannot safely become a Canvas receipt. */
export class CanvasProjectionReadError extends Error {
  constructor(
    readonly code: 'projection_unsupported' | 'projection_failed' | 'projection_malformed' | 'projection_revision_stale',
    message: string,
  ) {
    super(message);
  }
}

export function isCanvasProjectionReadError(error: unknown): error is CanvasProjectionReadError {
  return error instanceof CanvasProjectionReadError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

/** The model-facing text of a block and, only when it was cut, the flag that says so. */
export function boundedText(value: string): Pick<CanvasProjectionBlockCommon, 'text' | 'textTruncated'> {
  return value.length <= MAX_MODEL_TEXT_LENGTH
    ? { text: value }
    : {
      text: value.slice(0, MAX_MODEL_TEXT_LENGTH),
      textTruncated: true,
    };
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const values: string[] = [];
  for (const entry of value) {
    const string = stringValue(entry);
    if (string !== undefined) values.push(string);
  }
  return values.length === 0 ? undefined : values.slice(0, MAX_PROJECTION_ANNOTATION_IDS);
}

function projectionOutcome(value: unknown): CanvasInspectionProjectionReceipt['outcome'] | undefined {
  return value === 'preserved'
    || value === 'preserved-with-warnings'
    || value === 'simplified'
    || value === 'no-op'
    ? value
    : undefined;
}

function projectionStatus(value: unknown): CanvasInspectionProjectionReceipt['status'] | undefined {
  return value === 'success' || value === 'warning' ? value : undefined;
}

function projectionDiagnostics(value: unknown): CanvasInspectionProjectionReceipt['diagnostics'] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, MAX_PROJECTION_DIAGNOSTICS).flatMap((diagnostic) => {
    if (!isRecord(diagnostic)) return [];
    const code = stringValue(diagnostic.code);
    const severity = diagnostic.severity;
    if (code === undefined || (severity !== 'error' && severity !== 'warning' && severity !== 'info') || typeof diagnostic.lossy !== 'boolean') {
      return [];
    }
    return [{ code, severity, lossy: diagnostic.lossy }];
  });
}

/**
 * Image placeholders announced for the body story, in document order.
 *
 * Read from the raw diagnostics before they are reduced for the receipt: only
 * here do they still carry the source block and the output range.
 */
function imagePlaceholders(value: unknown, contentLength: number): CanvasMarkdownProjection['emplacementsDImage'] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((diagnostic) => {
    if (!isRecord(diagnostic) || diagnostic.code !== 'projection-placeholder-emitted' || diagnostic.construct !== 'image') return [];
    const source = isRecord(diagnostic.source) ? diagnostic.source : {};
    const story = isRecord(source.story) ? source.story : {};
    const blockId = stringValue(source.blockId);
    const range = outputRange(isRecord(diagnostic.output) ? diagnostic.output.range : undefined, contentLength);
    if (story.storyType !== 'body' || blockId === undefined || range === undefined) return [];
    return [{ blockId, debut: range.start, fin: range.end }];
  });
}

function outputRange(value: unknown, contentLength: number): { start: number; end: number } | undefined {
  if (!isRecord(value)) return undefined;
  const { start, end } = value;
  if (typeof start !== 'number' || typeof end !== 'number') return undefined;
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end < start || end > contentLength) return undefined;
  return { start, end };
}

/**
 * Parses a provider block map without attempting a textual or positional join.
 *
 * JavaScript string offsets are UTF-16 code units, the same coordinate space
 * documented by SuperDoc for `projection.blocks[].output`; `slice` therefore
 * preserves the provider's direct mapping even around astral characters.
 */
function projectionBlock(raw: unknown, content: string): CanvasProjectionBlock | undefined {
  if (!isRecord(raw)) return undefined;
  const nodeType = stringValue(raw.nodeType);
  const range = outputRange(raw.output, content.length);
  if (nodeType === undefined || range === undefined) return undefined;
  const fullText = content.slice(range.start, range.end);
  const common: CanvasProjectionBlockCommon = {
    nodeType,
    outputRange: range,
    fullText,
    ...boundedText(fullText),
    ...(stringValue(raw.parentBlockId) === undefined ? {} : { parentBlockId: stringValue(raw.parentBlockId) }),
    ...(stringArray(raw.changeIds) === undefined ? {} : { changeIds: stringArray(raw.changeIds) }),
    ...(stringArray(raw.commentIds) === undefined ? {} : { commentIds: stringArray(raw.commentIds) }),
  };
  if (raw.identity === 'public') {
    const nodeId = stringValue(raw.blockId);
    return nodeId === undefined ? undefined : { ...common, identity: 'public', nodeId };
  }
  if (raw.identity === 'unavailable' && raw.identityUnavailableReason === 'positionDerivedFallback') {
    return { ...common, identity: 'unavailable', identityUnavailableReason: 'positionDerivedFallback' };
  }
  return undefined;
}

function fallbackWithoutPublicIdentity(content: string): CanvasUnavailableProjectionBlock[] {
  if (content.length === 0) return [];
  return [{
    identity: 'unavailable',
    identityUnavailableReason: 'positionDerivedFallback',
    nodeType: 'document',
    outputRange: { start: 0, end: content.length },
    fullText: content,
    ...boundedText(content),
  }];
}

/** Removes selector-only complete text before a projection crosses the agent boundary. */
export function toModelSafeCanvasProjectionBlocks(
  blocks: readonly CanvasProjectionBlock[],
): ModelSafeCanvasProjectionBlock[] {
  return blocks.map(({ fullText, outputRange: _outputRange, ...block }) => {
    const textListPrefix = textListPrefixFromProjection({ nodeType: block.nodeType, fullText });
    return { ...block, ...(textListPrefix === undefined ? {} : { textListPrefix }) };
  });
}

/**
 * Reads exactly one native SuperDoc Markdown projection.
 *
 * No `blocks.list()` data is used to complete this result. A missing or
 * malformed public identity is deliberately degraded to readable but
 * non-targetable content; guessing it from nearby text, ordinals or offsets
 * would create a mutation address that SuperDoc never issued.
 */
export async function lireProjectionMarkdown(
  doc: SuperDocDocumentApi,
  mode: CanvasInspectionProjection,
): Promise<CanvasMarkdownProjection> {
  if (typeof doc.projectMarkdown !== 'function') {
    throw new CanvasProjectionReadError(
      'projection_unsupported',
      `canvas_inspect requested projection=${mode}, but this mounted SuperDoc document does not expose editor.doc.projectMarkdown. Re-open the Canvas on a qualified SuperDoc runtime and inspect again.`,
    );
  }

  let raw: unknown;
  try {
    raw = await doc.projectMarkdown({ reviewMode: mode });
  } catch (error) {
    const cause = error instanceof Error ? error.message.slice(0, 300) : String(error).slice(0, 300);
    throw new CanvasProjectionReadError(
      'projection_failed',
      `SuperDoc projectMarkdown could not produce projection=${mode}. Provider error: ${cause}`,
    );
  }

  if (!isRecord(raw)) {
    throw new CanvasProjectionReadError('projection_malformed', `SuperDoc projectMarkdown returned no projection object for projection=${mode}.`);
  }
  if (raw.format !== 'markdown') {
    throw new CanvasProjectionReadError('projection_malformed', `SuperDoc projectMarkdown returned format=${String(raw.format)} instead of markdown for projection=${mode}.`);
  }
  if (raw.reviewMode !== mode) {
    throw new CanvasProjectionReadError('projection_malformed', `SuperDoc projectMarkdown returned reviewMode=${String(raw.reviewMode)} for requested projection=${mode}.`);
  }
  const content = typeof raw.content === 'string' ? raw.content : undefined;
  const evaluatedRevision = stringValue(raw.evaluatedRevision);
  if (content === undefined || evaluatedRevision === undefined) {
    throw new CanvasProjectionReadError('projection_malformed', `SuperDoc projectMarkdown omitted content or evaluatedRevision for projection=${mode}.`);
  }
  if (raw.status === 'failed') {
    throw new CanvasProjectionReadError('projection_failed', `SuperDoc projectMarkdown reported status=failed for projection=${mode} at evaluatedRevision=${evaluatedRevision}.`);
  }
  const status = projectionStatus(raw.status);
  const outcome = projectionOutcome(raw.outcome);
  if (status === undefined || outcome === undefined || typeof raw.lossy !== 'boolean') {
    throw new CanvasProjectionReadError('projection_malformed', `SuperDoc projectMarkdown returned an unsupported status, outcome, or lossy flag for projection=${mode}.`);
  }

  const rawBlocks = Array.isArray(raw.blocks) ? raw.blocks : [];
  const parsedBlocks = rawBlocks.map((entry) => projectionBlock(entry, content));
  // A partial map is more dangerous than no map: do not let a valid neighbouring
  // block make the model believe the rest was safely addressable. A complete
  // fallback still lets it read and request a fresh targeted final inspection.
  const hasMalformedBlock = parsedBlocks.some((entry) => entry === undefined);
  const topLevelBlocks = hasMalformedBlock
    ? []
    : parsedBlocks
      .filter((entry): entry is CanvasProjectionBlock => entry !== undefined)
      .filter((entry) => entry.parentBlockId === undefined);
  const hasCompleteTopLevelBlockMap = !hasMalformedBlock && topLevelBlocks.length > 0;

  return {
    projection: {
      mode,
      evaluatedRevision,
      status,
      outcome,
      lossy: raw.lossy,
      diagnostics: projectionDiagnostics(raw.diagnostics),
    },
    content,
    blocks: hasCompleteTopLevelBlockMap ? topLevelBlocks : fallbackWithoutPublicIdentity(content),
    hasCompleteTopLevelBlockMap,
    blocsDuCorps: new Set(hasMalformedBlock
      ? []
      : parsedBlocks.flatMap((entry) => (entry?.identity === 'public' ? [entry.nodeId] : []))),
    emplacementsDImage: imagePlaceholders(raw.diagnostics, content.length),
  };
}
